import { gql, TypedDocumentNode } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";

import courseQueryResult from "../../../tests/__results__/courseQueryResult";
import { BOOKMARKS_QUERY } from "../../components/Bookmarks/Bookmarks.queries";
import { NOTES_QUERY } from "../../components/Notes/Notes.queries";
import { CourseFragment } from "../../hooks/__generated__/CourseFragment";
import { TrainingInstanceFragment } from "../../hooks/__generated__/TrainingInstanceFragment";
import { MODULE_QUERY, useAppState } from "../../hooks/useAppState";
import { SLIDE_VIEWS_QUERY, slideViewsLoadingVar, useCourseProgress } from "../../hooks/useCourseProgress";
import { useTestMode } from "../../hooks/useTestMode";
import { getFirstSectionIndex } from "../../util/course";
import { CourseQuery_course, CourseQuery_instance } from "./__generated__/CourseQuery";
import { FullCourseQuery, FullCourseQueryVariables } from "./__generated__/FullCourseQuery";
import { InitCoursePageQuery, InitCoursePageQueryVariables } from "./__generated__/InitCoursePageQuery";

/**
 * Single query for initial Player page load.
 * Returns course, instance, lastView, and initialModule in one round-trip.
 */
export const INIT_COURSE_PAGE_QUERY: TypedDocumentNode<InitCoursePageQuery, InitCoursePageQueryVariables> = gql`
  query InitCoursePageQuery($courseId: Int!) {
    initCoursePage(courseId: $courseId) {
      course {
        id
        courseType
        shortName
        name
        trainingId
        availableResolutions
        fullVersion
        version
        complete
        messages {
          text
          html
          link
          startDateTime
          endDateTime
        }
        contributors {
          id
          bio {
            firstName
            lastName
          }
          profile {
            name
            image
            text
            link
          }
        }
        resources {
          title
          resourceHost
          linkJson
        }
        sections {
          id
          name
          number
          surveyUrl
          modules {
            id
            name
            type
            sectionId
            fullNumber
            duration
            quizScore
            confidenceRating {
              moduleId
              rating
            }
            slides {
              id
              moduleId
              name
              number
              fullNumber
              duration
              notes {
                slideId
                content
                updated
              }
              bookmarks {
                slideId
                timestamp
              }
            }
          }
        }
      }
      instance {
        id
        expirationDateTime
        userId
        progress {
          instanceId
          viewedSeconds
          totalSeconds
          viewedSlides
          totalSlides
          totalQuizzes
          passedQuizzes
          complete
          lastView {
            slideId
            resumeTime
            dateTimeViewed
          }
          moduleProgress {
            id
            name
            type
            fullNumber
            viewedSlides
            totalSlides
            quizScore
          }
        }
      }
      lastView {
        slideId
        moduleId
        resumeTime
        dateTimeViewed
      }
      initialModule {
        sectionId
        id
        name
        type
        baseUrl
        fullNumber
        quizInProgress
        quizScore
        videoSuffixes {
          mp4 {
            SD
            HD
            FHD
          }
          webm {
            SD
            HD
            FHD
          }
        }
        slides {
          id
          name
          number
          fullNumber
          duration
          thumbnailPath
          captionsUrl
          cloudflareVideo {
            signedUrl
            expiresAt
          }
          moduleId
        }
        cookies {
          key
          value
          path
          domain
          expires
          secure
        }
        captionPaths {
          language
          path
        }
      }
    }
  }
`;

export const FULL_COURSE_QUERY: TypedDocumentNode<FullCourseQuery, FullCourseQueryVariables> = gql`
  query FullCourseQuery($courseId: Int!) {
    course(courseId: $courseId) {
      trainingId
      contributors {
        id
        bio {
          firstName
          lastName
        }
        profile {
          name
          image
          text
          link
        }
      }
      resources {
        title
        resourceHost
        linkJson
      }
      messages {
        text
        html
        link
        startDateTime
        endDateTime
      }
      sections {
        id
        modules {
          id
          quizScore
          confidenceRating {
            moduleId
            rating
          }
          slides {
            id
            notes {
              slideId
              content
              updated
            }
            bookmarks {
              slideId
              timestamp
            }
          }
        }
      }
    }
  }
`;

export const hasSlideInCourse = (data: CourseQuery_course, slideIdToFind: string): boolean => {
  for (const section of data.sections) {
    for (const module of section.modules) {
      for (const slide of module.slides) {
        if (slide.id === slideIdToFind) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Resolve moduleId for a slide using course structure from InitCoursePage/course data.
 * Enables early MODULE_QUERY without waiting for setSlide.
 */
export const getModuleIdForSlide = (data: CourseQuery_course, slideIdToFind: string): string | null => {
  for (const section of data.sections) {
    for (const module of section.modules) {
      if (module.slides.some(slide => slide.id === slideIdToFind)) {
        return module.id;
      }
    }
  }
  return null;
};

export function useCourseQuery(): CourseFragment | undefined {
  const { testMode } = useTestMode();
  const {
    appState: { courseId, moduleId, slideId, instanceId, course, instance },
    setSlide,
    setInstance,
    setModule,
    setCourse,
  } = useAppState();
  const client = useApolloClient();
  const { updateCourseProgressState, syncCourseSlideViews } = useCourseProgress(
    courseId ?? undefined,
    course?.id,
    instanceId ?? undefined,
  );

  const [queryInstance, setQueryInstance] = useState<TrainingInstanceFragment>();
  const [queryCourse, setQueryCourse] = useState<CourseFragment>();
  const [courseData, setCourseData] = useState<CourseQuery_course>();
  const [loading, setLoading] = useState(false);
  const [queryError, setQueryError] = useState(false);

  const queryFullCourse = useCallback(async () => {
    try {
      setLoading(true);
      const result = await client.query({
        query: FULL_COURSE_QUERY,
        variables: { courseId },
        errorPolicy: "all",
      });

      // Check for null course data before setting
      if (result.data?.course && courseId) {
        setCourse(courseId);
        updateCourseProgressState();
        setQueryError(false);
      } else {
        console.error("Full course data is null/undefined", result.data);
        setQueryError(true);
      }
      setLoading(false);
    } catch (e) {
      console.error("ERROR", e);
      setQueryError(true);
      setLoading(false);
    }
  }, [courseId, updateCourseProgressState, client, setCourse]);

  const fetchInitialData = useCallback(async () => {
    if (!courseId) return;
    try {
      setLoading(true);
      const result = await client.query({
        query: INIT_COURSE_PAGE_QUERY,
        variables: { courseId },
        fetchPolicy: "network-only",
      });

      const payload = result.data?.initCoursePage;
      if (!payload?.course || !payload?.instance) {
        console.error("InitCoursePage data is null/undefined", result.data);
        setQueryError(true);
        setLoading(false);
        return;
      }

      const { course, instance, lastView, initialModule } = payload;
      setCourseData(course);
      setCourse(course.trainingId);
      setInstance(instance.id);
      setQueryError(false);

      // Pre-populate course(courseId) so useBookmarks/useNotes readQuery returns data on load
      client.writeQuery({ query: BOOKMARKS_QUERY, variables: { courseId }, data: { course } });
      client.writeQuery({ query: NOTES_QUERY, variables: { courseId }, data: { course } });

      // Prefer initCoursePage lastView; fall back to instance.progress.lastView only when this
      // enrollment has actually started, then fall back to the first module.
      const instanceLastView = instance.progress?.lastView;
      const instanceHasStarted =
        (instance.progress?.viewedSlides ?? 0) > 0 || (instance.progress?.viewedSeconds ?? 0) > 0;
      const effectiveLastView =
        lastView ??
        (instanceLastView && instanceHasStarted
          ? (() => {
              const moduleIdFromSlide = getModuleIdForSlide(course, instanceLastView.slideId);
              if (!moduleIdFromSlide) {
                console.warn("Last viewed slide not found in course data, falling back to first module", {
                  slideId: instanceLastView.slideId,
                  courseId,
                });
                return null;
              }
              return {
                slideId: instanceLastView.slideId,
                moduleId: moduleIdFromSlide,
                resumeTime: instanceLastView.resumeTime,
              };
            })()
          : null);
      const moduleId = effectiveLastView?.moduleId ?? initialModule?.id;
      if (moduleId) {
        if (initialModule?.id === moduleId) {
          client.writeQuery({
            query: MODULE_QUERY,
            variables: { moduleId, instanceId: instance.id, courseId },
            data: {
              module: {
                ...initialModule,
                lastView: initialModule.lastView ?? null,
                slides: initialModule.slides.map(slide => ({
                  ...slide,
                  lastView: slide.lastView ?? null,
                })),
              },
            },
          });
        }

        const hasResumePoint = !!effectiveLastView?.slideId;
        await setModule(moduleId, !hasResumePoint);
        if (hasResumePoint && effectiveLastView) {
          setSlide(effectiveLastView.slideId, effectiveLastView.resumeTime ?? undefined);
        }
      }
      setLoading(false);
    } catch (e) {
      console.error("ERROR", e);
      setQueryError(true);
      setLoading(false);
    }
  }, [courseId, client, setModule, setSlide, setCourse, setInstance]);

  useEffect(() => {
    if (testMode) {
      setCourse();
    } else if (courseId && !testMode && !loading && !queryError) {
      // InitCoursePageQuery returns course, instance, lastView, and initialModule — everything needed
      // for the video. When navigating from Library, course may already be in cache (from
      // CourseDetailsQuery/FullCourseQuery), causing us to skip fetchInitialData and run
      // queryFullCourse instead. That path never sets instance or module, so the video never loads.
      // Always run fetchInitialData when we lack instance or module.
      if (!course || !instance || !moduleId) {
        fetchInitialData();
      } else if (!("contributors" in course) || !course.contributors) {
        queryFullCourse();
      }
    }
  }, [
    courseId,
    testMode,
    queryFullCourse,
    fetchInitialData,
    course,
    instance,
    moduleId,
    loading,
    queryError,
    setCourse,
  ]);

  const initializeTestMode = useCallback(() => {
    if (!slideId) {
      setQueryCourse(course);
      const firstSectionIndex = getFirstSectionIndex(courseQueryResult.data.course.sections);
      const firstModuleId = courseQueryResult.data.course.sections[firstSectionIndex].modules[0].id;
      setModule(firstModuleId);
    }

    setQueryInstance(courseQueryResult.data.instance as unknown as CourseQuery_instance);
  }, [course, slideId, setModule]);

  const syncCourseData = useCallback(() => {
    if (course && course.id !== queryCourse?.id) {
      setQueryCourse(course);
    }
  }, [course, queryCourse?.id]);

  const initializeFromInstance = useCallback(async () => {
    if (!course || moduleId || slideId || !instance) return;

    if (instance.progress?.lastView) {
      const lastViewSlideId = instance.progress.lastView.slideId;
      const courseHasLastViewSlide = courseData ? hasSlideInCourse(courseData, lastViewSlideId) : false;

      if (courseData && !courseHasLastViewSlide) {
        console.warn("Last viewed slide not found in course data, falling back to first module", {
          slideId: lastViewSlideId,
          courseId,
        });
        const firstSectionIndex = getFirstSectionIndex(courseData.sections);
        const firstModuleId = courseData.sections[firstSectionIndex]?.modules[0]?.id;
        if (firstModuleId) {
          setModule(firstModuleId); // Fire in background for progressive loading
        }
        return;
      }

      // Fire MODULE_QUERY in background; setSlide runs when module is ready
      const lastViewModuleId = courseData ? getModuleIdForSlide(courseData, lastViewSlideId) : null;
      if (lastViewModuleId) {
        setModule(lastViewModuleId, false).then(() =>
          setSlide(lastViewSlideId, instance.progress?.lastView?.resumeTime ?? undefined),
        );
      }
      return;
    }

    const firstSectionIndex = getFirstSectionIndex(course.sections);
    const firstModuleId = course.sections[firstSectionIndex].modules[0].id;
    setModule(firstModuleId); // Fire in background for progressive loading
  }, [course, courseData, courseId, instance, moduleId, setModule, setSlide, slideId]);

  const syncInstance = useCallback(() => {
    if (instance && instance !== queryInstance) {
      setQueryInstance(instance);
      setInstance(instance.id);
    }
  }, [instance, queryInstance, setInstance]);

  useEffect(() => {
    if (testMode) {
      initializeTestMode();
      return;
    }

    syncCourseData();
    initializeFromInstance();
    syncInstance();
  }, [
    courseData,
    moduleId,
    slideId,
    testMode,
    course,
    instance,
    initializeFromInstance,
    initializeTestMode,
    syncCourseData,
    syncInstance,
  ]);

  // Fetch slide views: deferred so it doesn't compete with InitCoursePageQuery/ModuleQuery on critical path,
  // cache-first on initial fetch (skips network when cache warm), then refresh periodically
  const SLIDE_VIEWS_DEFER_MS = 2_500;
  const SLIDE_VIEWS_REFRESH_INTERVAL_MS = 60_000;

  useEffect(() => {
    if (testMode || !courseId || !instanceId || !course) return;

    // Set loading state for progress indicator (initial load only)
    slideViewsLoadingVar(true);

    const fetchSlideViews = (fetchPolicy: "cache-first" | "network-only") =>
      client
        .query({
          query: SLIDE_VIEWS_QUERY,
          variables: { courseId, instanceId },
          fetchPolicy,
        })
        .then(() => {
          syncCourseSlideViews();
          updateCourseProgressState();
        })
        .catch(error => {
          console.warn("Failed to fetch slide views for progress calculation:", error);
        });

    // Defer initial fetch so it doesn't compete with InitCoursePageQuery/ModuleQuery on critical path
    // Use network-only to ensure fresh data (stale slide views can overwrite reset progress)
    const timeoutId = globalThis.setTimeout(() => {
      fetchSlideViews("network-only").finally(() => {
        slideViewsLoadingVar(false);
      });
    }, SLIDE_VIEWS_DEFER_MS);

    // Periodic refresh to keep progress accurate (silent, no loading indicator)
    const intervalId = globalThis.setInterval(() => {
      fetchSlideViews("network-only");
    }, SLIDE_VIEWS_REFRESH_INTERVAL_MS);

    return () => {
      globalThis.clearTimeout(timeoutId);
      globalThis.clearInterval(intervalId);
    };
  }, [client, courseId, instanceId, course, testMode, syncCourseSlideViews, updateCourseProgressState]);

  return course;
}
